import React, {useState, useCallback, useMemo} from 'react';
import {View, StyleSheet, FlatList, RefreshControl, ScrollView, TouchableOpacity, Platform, Modal} from 'react-native';
import {Text, IconButton, Dialog, Portal, Button, Chip, TextInput} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import {colors, spacing} from '../../theme';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import {workerApi} from '../../services/api/workerApi';
import WorkAssignmentStatusDialog from '../order/WorkAssignmentStatusDialog';

const STATUSES = ['NOT_STARTED', 'STARTED', 'COMPLETED'];
const STATUS_LABELS = {
  NOT_STARTED: 'Not Started',
  STARTED: 'Started',
  COMPLETED: 'Completed',
};
const STATUS_ICONS = {
  NOT_STARTED: 'clock-outline',
  STARTED: 'progress-wrench',
  COMPLETED: 'check-circle-outline',
  DELETED: 'close-circle-outline',
};

const PAYMENT_OPTIONS = [
  {value: false, label: 'Unpaid'},
  {value: true, label: 'Paid'},
];

const getDefaultStartDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getDefaultEndDate = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const getStatusColor = status => {
  switch (status) {
    case 'COMPLETED': return colors.success;
    case 'STARTED': return colors.primary;
    case 'DELETED': return colors.error;
    default: return colors.textSecondary;
  }
};

const DressAssignmentsScreen = () => {
  const {t} = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusAssignment, setStatusAssignment] = useState(null);

  // Filter state
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterWorkerId, setFilterWorkerId] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterPaid, setFilterPaid] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(getDefaultStartDate);
  const [filterEndDate, setFilterEndDate] = useState(getDefaultEndDate);
  const [filterOrderId, setFilterOrderId] = useState('');

  // Temp filter state
  const [tmpWorkerId, setTmpWorkerId] = useState(null);
  const [tmpStatus, setTmpStatus] = useState(null);
  const [tmpPaid, setTmpPaid] = useState(null);
  const [tmpStartDate, setTmpStartDate] = useState(getDefaultStartDate);
  const [tmpEndDate, setTmpEndDate] = useState(getDefaultEndDate);
  const [tmpOrderId, setTmpOrderId] = useState('');

  // Date picker
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [datePickerTarget, setDatePickerTarget] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState(new Date());

  const hasFilters = filterWorkerId || filterStatus || filterPaid !== null || filterStartDate || filterEndDate || filterOrderId;

  const workerMap = useMemo(() => {
    const map = {};
    workers.forEach(w => { map[w.id] = w.fullName; });
    return map;
  }, [workers]);

  const workerSummary = useMemo(() => {
    const counts = {};
    assignments.forEach(a => {
      if (!counts[a.workerId]) {
        counts[a.workerId] = {id: a.workerId, name: a.workerName, count: 0};
      }
      counts[a.workerId].count += 1;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count);
  }, [assignments]);

  const buildParams = useCallback((wId, st, pd, sd, ed, oid) => {
    const params = {};
    if (wId) params.workerId = wId;
    if (st) params.status = st;
    if (pd !== null) params.paid = pd;
    if (sd) params.startDate = formatDateISO(sd);
    if (ed) params.endDate = formatDateISO(ed);
    if (oid) params.orderId = oid;
    return Object.keys(params).length > 0 ? params : undefined;
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      const params = buildParams(filterWorkerId, filterStatus, filterPaid, filterStartDate, filterEndDate, filterOrderId);
      const res = await workAssignmentApi.getAll(params);
      setAssignments(res.data?.data || []);
    } catch (error) {
      // silently fail on refresh
    }
  }, [filterWorkerId, filterStatus, filterPaid, filterStartDate, filterEndDate, filterOrderId, buildParams]);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await workerApi.search({page: 0, size: 200, isActive: true});
      setWorkers(res.data?.data?.content || []);
    } catch (e) {}
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchAssignments();
      fetchWorkers();
    }, [fetchAssignments, fetchWorkers]),
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchAssignments();
    setRefreshing(false);
  };

  const openFilterModal = useCallback(() => {
    setTmpWorkerId(filterWorkerId);
    setTmpStatus(filterStatus);
    setTmpPaid(filterPaid);
    setTmpStartDate(filterStartDate);
    setTmpEndDate(filterEndDate);
    setTmpOrderId(filterOrderId);
    setFilterVisible(true);
  }, [filterWorkerId, filterStatus, filterPaid, filterStartDate, filterEndDate, filterOrderId]);

  const applyFilters = useCallback(() => {
    setFilterWorkerId(tmpWorkerId);
    setFilterStatus(tmpStatus);
    setFilterPaid(tmpPaid);
    setFilterStartDate(tmpStartDate);
    setFilterEndDate(tmpEndDate);
    setFilterOrderId(tmpOrderId);
    setFilterVisible(false);
  }, [tmpWorkerId, tmpStatus, tmpPaid, tmpStartDate, tmpEndDate, tmpOrderId]);

  const clearFilters = useCallback(() => {
    setFilterWorkerId(null);
    setFilterStatus(null);
    setFilterPaid(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterOrderId('');
    setTmpWorkerId(null);
    setTmpStatus(null);
    setTmpPaid(null);
    setTmpStartDate(null);
    setTmpEndDate(null);
    setTmpOrderId('');
    setFilterVisible(false);
  }, []);

  const openDatePicker = useCallback((target) => {
    const currentVal = target === 'start' ? tmpStartDate : tmpEndDate;
    setDatePickerTarget(target);
    setDatePickerValue(currentVal || new Date());
    setFilterVisible(false);
    setTimeout(() => setDatePickerVisible(true), 300);
  }, [tmpStartDate, tmpEndDate]);

  const handleDatePickerConfirm = useCallback(() => {
    if (datePickerTarget === 'start') setTmpStartDate(datePickerValue);
    else if (datePickerTarget === 'end') setTmpEndDate(datePickerValue);
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget, datePickerValue]);

  const handleDatePickerCancel = useCallback(() => {
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, []);

  const handleAndroidDateChange = useCallback((event, date) => {
    if (event.type === 'dismissed') {
      setDatePickerVisible(false);
      setTimeout(() => setFilterVisible(true), 300);
      return;
    }
    if (date) {
      if (datePickerTarget === 'start') setTmpStartDate(date);
      else if (datePickerTarget === 'end') setTmpEndDate(date);
    }
    setDatePickerVisible(false);
    setTimeout(() => setFilterVisible(true), 300);
  }, [datePickerTarget]);

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const formatDateShort = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const formatDateDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', {year: 'numeric', month: 'short', day: 'numeric'});
  };

  const renderItem = ({item}) => {
    const statusColor = getStatusColor(item.status);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.card}
        onPress={() => {
          setStatusAssignment(item);
          setStatusDialogVisible(true);
        }}>
        <View style={styles.cardTop}>
          <View style={styles.cardLeft}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {item.workerName?.charAt(0)?.toUpperCase() || '?'}
              </Text>
            </View>
          </View>
          <View style={styles.cardBody}>
            <Text style={styles.workerName} numberOfLines={1}>{item.workerName}</Text>
            <View style={styles.metaRow}>
              {item.workerRole && (
                <View style={styles.roleTag}>
                  <MaterialCommunityIcons name="account-outline" size={11} color={colors.textSecondary} />
                  <Text style={styles.roleText}>{item.workerRole}</Text>
                </View>
              )}
              {item.assignedAt && (
                <View style={styles.dateTag}>
                  <MaterialCommunityIcons name="calendar-outline" size={11} color={colors.textSecondary} />
                  <Text style={styles.dateText}>{formatDate(item.assignedAt)}</Text>
                </View>
              )}
            </View>
          </View>
          {item.pieceRate != null && (
            <View style={styles.rateBox}>
              <Text style={styles.rateAmount}>{'\u20B9'}{Number(item.pieceRate)}</Text>
              <Text style={styles.rateLabel}>rate</Text>
            </View>
          )}
        </View>
        <View style={styles.cardBottom}>
          <View style={styles.itemInfo}>
            <MaterialCommunityIcons name="hanger" size={14} color={colors.primary} />
            <Text style={styles.itemName} numberOfLines={1}>{item.catalogItemName || '-'}</Text>
            {item.instanceCode && (
              <Text style={styles.instanceCode}>{item.instanceCode}</Text>
            )}
          </View>
          <View style={[styles.statusBadge, {backgroundColor: statusColor + '15'}]}>
            <MaterialCommunityIcons
              name={STATUS_ICONS[item.status] || 'help-circle-outline'}
              size={14}
              color={statusColor}
            />
            <Text style={[styles.statusText, {color: statusColor}]}>
              {STATUS_LABELS[item.status] || item.status?.replace('_', ' ')}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      {/* Filter chips bar */}
      <View style={styles.chipBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipScroll}>
          {filterWorkerId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterWorkerId(null)}>
              {workerMap[filterWorkerId] || 'Worker'}
            </Chip>
          )}
          {filterStatus && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStatus(null)}>
              {STATUS_LABELS[filterStatus]}
            </Chip>
          )}
          {filterPaid !== null && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterPaid(null)}>
              {filterPaid ? 'Paid' : 'Unpaid'}
            </Chip>
          )}
          {filterStartDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStartDate(null)}>
              From: {formatDateShort(filterStartDate)}
            </Chip>
          )}
          {filterEndDate && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterEndDate(null)}>
              To: {formatDateShort(filterEndDate)}
            </Chip>
          )}
          {filterOrderId ? (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterOrderId('')}>
              Order: {filterOrderId}
            </Chip>
          ) : null}
          {hasFilters && (
            <TouchableOpacity onPress={clearFilters} style={styles.clearBtn}>
              <Text style={styles.clearText}>Clear</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
        <IconButton
          icon={hasFilters ? 'filter-check' : 'filter-variant'}
          size={22}
          iconColor={hasFilters ? colors.primary : colors.textSecondary}
          onPress={openFilterModal}
          style={styles.filterIconBtn}
        />
      </View>

      {/* Worker summary strip */}
      {workerSummary.length > 0 && (
        <View style={styles.summaryStrip}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.summaryScroll}>
            {workerSummary.map(ws => {
              const isActive = filterWorkerId === ws.id;
              return (
                <TouchableOpacity
                  key={ws.id}
                  activeOpacity={0.7}
                  style={[styles.summaryPill, isActive && styles.summaryPillActive]}
                  onPress={() => setFilterWorkerId(isActive ? null : ws.id)}>
                  <View style={[styles.summaryAvatar, isActive && styles.summaryAvatarActive]}>
                    <Text style={[styles.summaryAvatarText, isActive && {color: colors.white}]}>
                      {ws.name?.charAt(0)?.toUpperCase() || '?'}
                    </Text>
                  </View>
                  <Text style={[styles.summaryName, isActive && {color: colors.white}]} numberOfLines={1}>{ws.name}</Text>
                  <View style={[styles.summaryCount, isActive && styles.summaryCountActive]}>
                    <Text style={[styles.summaryCountText, isActive && {color: colors.primary}]}>{ws.count}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      )}

      <FlatList
        data={assignments}
        keyExtractor={item => String(item.id)}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="hanger" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('common.noData')}</Text>
          </View>
        }
      />

      {statusAssignment && (
        <WorkAssignmentStatusDialog
          visible={statusDialogVisible}
          onDismiss={() => setStatusDialogVisible(false)}
          assignment={statusAssignment}
          onStatusChanged={fetchAssignments}
        />
      )}

      {/* Filter Modal */}
      <Portal>
        <Dialog visible={filterVisible} onDismiss={() => setFilterVisible(false)} style={{borderRadius: 15}}>
          <View style={styles.dialogTitleRow}>
            <Text variant="titleLarge" style={{fontWeight: '600'}}>Filter Assignments</Text>
            <IconButton icon="close" size={22} onPress={() => setFilterVisible(false)} style={{margin: 0}} />
          </View>
          <Dialog.ScrollArea style={{maxHeight: 460, paddingHorizontal: 0}}>
            <ScrollView>
              <View style={styles.filterSection}>
                <Text variant="labelLarge" style={styles.filterLabel}>Worker</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {workers.map(w => {
                      const selected = tmpWorkerId === w.id;
                      return (
                        <TouchableOpacity
                          key={w.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setTmpWorkerId(selected ? null : w.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {w.fullName}
                          </Text>
                          {selected && <IconButton icon="check" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>Assignment Date</Text>
                <View style={styles.dateRow}>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('start')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpStartDate ? styles.dateTextFilter : styles.datePlaceholder}>
                      {tmpStartDate ? formatDateDisplay(tmpStartDate) : 'Start date'}
                    </Text>
                    {tmpStartDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpStartDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                  <Text style={styles.dateSep}>to</Text>
                  <TouchableOpacity style={styles.dateInput} onPress={() => openDatePicker('end')}>
                    <IconButton icon="calendar" size={16} iconColor={colors.primary} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    <Text style={tmpEndDate ? styles.dateTextFilter : styles.datePlaceholder}>
                      {tmpEndDate ? formatDateDisplay(tmpEndDate) : 'End date'}
                    </Text>
                    {tmpEndDate && (
                      <IconButton icon="close" size={14} onPress={(e) => { e?.stopPropagation?.(); setTmpEndDate(null); }} style={{margin: 0, padding: 0, width: 20, height: 20}} />
                    )}
                  </TouchableOpacity>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>Status</Text>
                <View style={styles.filterChipGroup}>
                  {STATUSES.map(s => (
                    <Chip key={s} selected={tmpStatus === s}
                      onPress={() => setTmpStatus(tmpStatus === s ? null : s)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {STATUS_LABELS[s]}
                    </Chip>
                  ))}
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>Payment Status</Text>
                <View style={styles.filterChipGroup}>
                  {PAYMENT_OPTIONS.map(p => (
                    <Chip key={String(p.value)} selected={tmpPaid === p.value}
                      onPress={() => setTmpPaid(tmpPaid === p.value ? null : p.value)}
                      style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                      {p.label}
                    </Chip>
                  ))}
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>Order ID</Text>
                <TextInput
                  mode="outlined"
                  placeholder="Enter order ID"
                  value={tmpOrderId}
                  onChangeText={setTmpOrderId}
                  keyboardType="numeric"
                  dense
                  style={styles.orderIdInput}
                />
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={clearFilters}>Clear Filters</Button>
            <Button mode="contained" onPress={applyFilters}
              style={{borderRadius: 10}} buttonColor={colors.primary}>
              Apply Filters
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Date picker */}
      {Platform.OS === 'ios' ? (
        <Modal visible={datePickerVisible} transparent animationType="slide">
          <View style={styles.datePickerOverlay}>
            <View style={styles.datePickerSheet}>
              <View style={styles.datePickerHeader}>
                <Button onPress={handleDatePickerCancel}>Cancel</Button>
                <Text variant="titleMedium" style={{fontWeight: '600'}}>
                  {datePickerTarget === 'start' ? 'Start Date' : 'End Date'}
                </Text>
                <Button onPress={handleDatePickerConfirm}>Done</Button>
              </View>
              <DateTimePicker
                value={datePickerValue}
                mode="date"
                display="spinner"
                onChange={(event, date) => { if (date) setDatePickerValue(date); }}
                style={{height: 200}}
              />
            </View>
          </View>
        </Modal>
      ) : (
        datePickerVisible && (
          <DateTimePicker
            value={datePickerValue}
            mode="date"
            display="default"
            onChange={handleAndroidDateChange}
          />
        )
      )}
    </View>
  );
};

const formatDateISO = date => {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},

  // Filter bar
  chipBar: {
    flexDirection: 'row', alignItems: 'center',
    paddingLeft: spacing.sm, backgroundColor: colors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  chipScroll: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.xs, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.primary + '20', height: 30},
  filterChipText: {fontSize: 12},

  // Worker summary strip
  summaryStrip: {
    backgroundColor: colors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  summaryScroll: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  summaryPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    borderRadius: 20,
    paddingVertical: 4,
    paddingLeft: 4,
    paddingRight: 10,
    gap: 6,
  },
  summaryPillActive: {
    backgroundColor: colors.primary,
  },
  summaryAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  summaryAvatarActive: {
    backgroundColor: colors.white + '30',
  },
  summaryAvatarText: {fontSize: 11, fontWeight: '700', color: colors.primary},
  summaryName: {fontSize: 12, fontWeight: '600', color: colors.text, maxWidth: 80},
  summaryCount: {
    backgroundColor: colors.primary + '15',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  summaryCountActive: {
    backgroundColor: colors.white,
  },
  summaryCountText: {fontSize: 11, fontWeight: '700', color: colors.primary},
  clearBtn: {justifyContent: 'center', paddingHorizontal: spacing.xs},
  clearText: {color: colors.primary, fontSize: 12, fontWeight: '600'},
  filterIconBtn: {margin: 0},

  // List
  listContent: {padding: spacing.sm, paddingBottom: spacing.xl, gap: spacing.sm},

  // Card
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  cardLeft: {},
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {fontSize: 16, fontWeight: '700', color: colors.primary},
  cardBody: {flex: 1},
  workerName: {fontSize: 15, fontWeight: '600', color: colors.text},
  metaRow: {flexDirection: 'row', flexWrap: 'wrap', marginTop: 3, gap: 6},
  roleTag: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: colors.background, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
  },
  roleText: {fontSize: 11, color: colors.textSecondary, fontWeight: '500'},
  dateTag: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
  },
  dateText: {fontSize: 11, color: colors.textSecondary},
  rateBox: {alignItems: 'center'},
  rateAmount: {fontSize: 15, fontWeight: '700', color: colors.text},
  rateLabel: {fontSize: 10, color: colors.textSecondary},

  // Card bottom
  cardBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    marginLeft: 48,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.divider,
  },
  itemInfo: {flexDirection: 'row', alignItems: 'center', flex: 1, gap: 4},
  itemName: {fontSize: 13, fontWeight: '500', color: colors.text, flex: 1},
  instanceCode: {fontSize: 11, color: colors.textSecondary, marginLeft: 4},
  statusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6,
  },
  statusText: {fontSize: 12, fontWeight: '600'},

  // Empty
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },

  // Filter modal
  dialogTitleRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.lg, paddingTop: spacing.md, paddingBottom: spacing.xs,
  },
  filterSection: {paddingHorizontal: spacing.lg, paddingVertical: spacing.sm},
  filterLabel: {fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.xs},
  filterChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  filterSelectChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  filterListBox: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, maxHeight: 100, overflow: 'hidden',
  },
  filterListItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5, paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  filterListItemSelected: {backgroundColor: colors.primary + '12'},
  filterListItemText: {fontSize: 14, color: colors.text},
  filterListItemTextSelected: {fontWeight: '600', color: colors.primary},
  dateRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  dateInput: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4,
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, paddingHorizontal: 6, paddingVertical: 8,
  },
  dateTextFilter: {fontSize: 12, color: colors.text, flex: 1},
  datePlaceholder: {fontSize: 12, color: colors.textSecondary, flex: 1},
  dateSep: {fontSize: 13, color: colors.textSecondary},
  orderIdInput: {backgroundColor: colors.surface, fontSize: 14},

  // Date picker (iOS)
  datePickerOverlay: {
    flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.4)',
  },
  datePickerSheet: {
    backgroundColor: colors.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16,
    paddingBottom: 30,
  },
  datePickerHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
});

export default DressAssignmentsScreen;
